import { useEffect, type FC } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'
import adminQueries from '@/modules/admin/queries'
import type { ConsumptionReport, ReportFilters } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import { useReportsContext } from '../index.context'

export const Consumption: FC<{
  filters: ReportFilters
}> = ({ filters }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  const stringMatcher = (item: ConsumptionReport) => [
    item.code,
    item.name,
    item.employeeNo,
    item.benefitMobile,
    item.taxReportAmount,
    item.subscriptionAmount,
    item.totalAmount,
    item.emne,
  ]

  const { data: reports = [] } = useQuery(
    adminQueries.adminConsumptionReports(filters),
    async () => {
      const reports = await adminResources.getConsumptionReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })
  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)
  const total = reportsFiltered.length
  const { formatNumber } = useFormattedNumberWithCurrency()

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="code">
                <Translation id="Code" />
              </Th>
              <Th onClick={handleSort} id="name">
                <Translation id="Name" />
              </Th>
              <Th onClick={handleSort} id="employeeNo">
                <Translation id="Employee Number" />
              </Th>
              <Th onClick={handleSort} id="benefitMobile">
                <Translation id="Benefit Mobile" />
              </Th>
              <Th onClick={handleSort} id="taxReportAmount" className="text-right">
                <Translation id="Tax Report Amount" />
              </Th>
              <Th onClick={handleSort} id="subscriptionAmount" className="text-right">
                <Translation id="Subscription Amount" />
              </Th>
              <Th onClick={handleSort} id="totalAmount" className="text-right">
                <Translation id="Total Amount" />
              </Th>
              <Th onClick={handleSort} id="emne">
                <Translation id="EMNE" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList.map(
              (
                {
                  code,
                  name,
                  employeeNo,
                  benefitMobile,
                  taxReportAmount,
                  subscriptionAmount,
                  totalAmount,
                  emne,
                },
                index
              ) => (
                <tr key={`${code}-${index}`}>
                  <Td>
                    <Highlight text={code} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={name} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={employeeNo} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight text={benefitMobile} pattern={searchFilter} />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(taxReportAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(subscriptionAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(totalAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td>{emne}</Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
